import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClientCompaniesTable from "../components/ClientCompaniesTable";
import PageSectionCard from "../components/PageSectionCard";
import Pagination from "../components/Pagination";
import { useAppSession } from "../context/AppSessionContext";
import { readErrorMessage } from "../lib/http";
import { ClientCompany, PaginatedResponse } from "../types/clientCompanies";

const defaultPageSize = 20;

function ClientCompaniesListPage() {
  const { authorizedFetch, canManageClientCompanies } = useAppSession();
  const [companies, setCompanies] = useState<ClientCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadCompanies = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const query = new URLSearchParams({
        includeInactive: "true",
        page: String(page),
        limit: String(defaultPageSize),
      });
      const response = await authorizedFetch(
        `/client-companies?${query.toString()}`,
      );

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to load client companies (${response.status}).`,
          ),
        );
      }

      const payload =
        (await response.json()) as PaginatedResponse<ClientCompany>;
      const responseTotalPages = payload.totalPages ?? 0;

      if (responseTotalPages > 0 && page > responseTotalPages) {
        setPage(responseTotalPages);
        return;
      }

      setCompanies(payload.data ?? []);
      setTotal(payload.total ?? 0);
      setTotalPages(responseTotalPages);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load client companies.";
      setLoadError(message);
      setCompanies([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [authorizedFetch, page]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  return (
    <PageSectionCard
      titleId="list-title"
      eyebrow="Client companies"
      title="Client companies"
      lead="Manage client company records and open edit forms."
      actions={
        <div className="headerActions">
          {canManageClientCompanies ? (
            <Link className="primaryButton" to="/client-companies/new">
              Create
            </Link>
          ) : (
            <span className="primaryButton disabledAction">Create</span>
          )}
        </div>
      }
    >
      {isLoading ? <p>Loading companies...</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {!isLoading && !loadError && companies.length === 0 ? (
        <div className="emptyState">
          <p>No client companies found yet.</p>
          {canManageClientCompanies ? (
            <p className="hintText">
              Use the Create button above to add a client company.
            </p>
          ) : (
            <p className="hintText">
              Ask a manager/admin to create the first client company.
            </p>
          )}
        </div>
      ) : null}

      {!isLoading && !loadError && companies.length > 0 ? (
        <ClientCompaniesTable
          companies={companies}
          canManageClientCompanies={canManageClientCompanies}
        />
      ) : null}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={isLoading}
        summary={`${total} compan${total === 1 ? "y" : "ies"}`}
      />
    </PageSectionCard>
  );
}

export default ClientCompaniesListPage;
